var __runInitializers = (this && this.__runInitializers) || function (thisArg, initializers, value) {
    var useValue = arguments.length > 2;
    for (var i = 0; i < initializers.length; i++) {
        value = useValue ? initializers[i].call(thisArg, value) : initializers[i].call(thisArg);
    }
    return useValue ? value : void 0;
};
var __esDecorate = (this && this.__esDecorate) || function (ctor, descriptorIn, decorators, contextIn, initializers, extraInitializers) {
    function accept(f) { if (f !== void 0 && typeof f !== "function") throw new TypeError("Function expected"); return f; }
    var kind = contextIn.kind, key = kind === "getter" ? "get" : kind === "setter" ? "set" : "value";
    var target = !descriptorIn && ctor ? contextIn["static"] ? ctor : ctor.prototype : null;
    var descriptor = descriptorIn || (target ? Object.getOwnPropertyDescriptor(target, contextIn.name) : {});
    var _, done = false;
    for (var i = decorators.length - 1; i >= 0; i--) {
        var context = {};
        for (var p in contextIn) context[p] = p === "access" ? {} : contextIn[p];
        for (var p in contextIn.access) context.access[p] = contextIn.access[p];
        context.addInitializer = function (f) { if (done) throw new TypeError("Cannot add initializers after decoration has completed"); extraInitializers.push(accept(f || null)); };
        var result = (0, decorators[i])(kind === "accessor" ? { get: descriptor.get, set: descriptor.set } : descriptor[key], context);
        if (kind === "accessor") {
            if (result === void 0) continue;
            if (result === null || typeof result !== "object") throw new TypeError("Object expected");
            if (_ = accept(result.get)) descriptor.get = _;
            if (_ = accept(result.set)) descriptor.set = _;
            if (_ = accept(result.init)) initializers.unshift(_);
        }
        else if (_ = accept(result)) {
            if (kind === "field") initializers.unshift(_);
            else descriptor[key] = _;
        }
    }
    if (target) Object.defineProperty(target, contextIn.name, descriptor);
    done = true;
};
var __setFunctionName = (this && this.__setFunctionName) || function (f, name, prefix) {
    if (typeof name === "symbol") name = name.description ? "[".concat(name.description, "]") : "";
    return Object.defineProperty(f, "name", { configurable: true, value: prefix ? "".concat(prefix, " ", name) : name });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
import { html, LitElement } from 'lit';
import { property, query, queryAssignedElements, state } from 'lit/decorators.js';
import { isNil, uniqueId } from 'lodash-es';
import { SdsContextMenuGroup } from '../../context-menu/group/sds-cm-group.lit';
import { SdsContextMenuSelectItem, } from '../../context-menu/item/sds-cm-select-item.lit';
import { SdsContextMenuItem, } from '../../context-menu/item/sds-cm-item.lit';
import '../../icon/sds-icon.lit';
import styles from '../sds-select.lit.scss';
import { SdsContextMenuSkeletonItem } from '../../context-menu/item/sds-cm-skeleton-item.lit';
import { translate } from '../../utilities/translate';
import { MultiselectRenderer } from './multiselect-renderer';
import { SimpleMultiselectRenderer } from '../simple-multiselect/simple-multiselect-renderer';
import { SortInfo } from '../../shared/context-menu/item-search-sort';
import { sdsCustomElement } from '../../utilities/custom-components';
import { cleanMenuItemsFocus, getFocusedItem, initMenuItemsFocus } from '../menu-items-state-handler';
/**
 * `sds-multi-select` is a web component designed for selecting multiple options from a list.
 * It enables users to efficiently choose one or more items from a variety of selections,
 * making it ideal for scenarios like filtering, tagging, or preferences. The component
 * supports features such as selection management, customizable hints, and accessibility improvements.
 *
 * ### Attributes
 * - `hint`: A hint that provides context or guidance to users about the multi-select input.
 * - `disabled`: A boolean that indicates if the multi-select is disabled. Defaults to `false`.
 * - `with-select-all`: A boolean that specifies whether to include a "Select All" option.
 * - `simple`: A boolean that toggles a simplified mode for the multi-select. Defaults to `false`.
 * - `selected-items-name`: The name used to identify the selected items.
 * - `selected`: A boolean indicating if the multi-select is currently selected. Defaults to `false`.
 * - `placeholder`: The placeholder text for the input field when no items are selected.
 * - `error-message`: An error message displayed when there is a validation issue.
 * - `input-value`: The current value of the input field.
 * - `invalid`: A boolean that indicates if the selection is invalid. Defaults to `false`.
 * - `max-width`: The maximum width of the multi-select component in pixels. Defaults to `400`.
 * - `only-icon`: A boolean that indicates if the multi-select should only display icons. Defaults to `false`.
 * - `item-icon`: The icon associated with each item in the multi-select.
 * - `value`: The current value of the multi-select, which can be bound to the selected options.
 *
 * @tag sds-multi-select
 */
let SdsMultiselect = (() => {
    var _SdsMultiselect_hint_accessor_storage, _SdsMultiselect_disabled_accessor_storage, _SdsMultiselect_withSelectAll_accessor_storage, _SdsMultiselect_simple_accessor_storage, _SdsMultiselect_selectedItemsName_accessor_storage, _SdsMultiselect_placeholder_accessor_storage, _SdsMultiselect_errorMessage_accessor_storage, _SdsMultiselect_inputValue_accessor_storage, _SdsMultiselect_invalid_accessor_storage, _SdsMultiselect_maxWidth_accessor_storage, _SdsMultiselect_onlyIcon_accessor_storage, _SdsMultiselect_itemIcon_accessor_storage, _SdsMultiselect_trigger_accessor_storage, _SdsMultiselect_inputComponent_accessor_storage, _SdsMultiselect__contextMenu_accessor_storage, _SdsMultiselect_selectItemsFromDoc_accessor_storage, _SdsMultiselect__selectAllItem_accessor_storage, _SdsMultiselect__value_accessor_storage;
    let _classDecorators = [sdsCustomElement('sds-multi-select')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = LitElement;
    let _instanceExtraInitializers = [];
    let _hint_decorators;
    let _hint_initializers = [];
    let _hint_extraInitializers = [];
    let _disabled_decorators;
    let _disabled_initializers = [];
    let _disabled_extraInitializers = [];
    let _withSelectAll_decorators;
    let _withSelectAll_initializers = [];
    let _withSelectAll_extraInitializers = [];
    let _simple_decorators;
    let _simple_initializers = [];
    let _simple_extraInitializers = [];
    let _selectedItemsName_decorators;
    let _selectedItemsName_initializers = [];
    let _selectedItemsName_extraInitializers = [];
    let _placeholder_decorators;
    let _placeholder_initializers = [];
    let _placeholder_extraInitializers = [];
    let _errorMessage_decorators;
    let _errorMessage_initializers = [];
    let _errorMessage_extraInitializers = [];
    let _inputValue_decorators;
    let _inputValue_initializers = [];
    let _inputValue_extraInitializers = [];
    let _invalid_decorators;
    let _invalid_initializers = [];
    let _invalid_extraInitializers = [];
    let _maxWidth_decorators;
    let _maxWidth_initializers = [];
    let _maxWidth_extraInitializers = [];
    let _onlyIcon_decorators;
    let _onlyIcon_initializers = [];
    let _onlyIcon_extraInitializers = [];
    let _itemIcon_decorators;
    let _itemIcon_initializers = [];
    let _itemIcon_extraInitializers = [];
    let _trigger_decorators;
    let _trigger_initializers = [];
    let _trigger_extraInitializers = [];
    let _inputComponent_decorators;
    let _inputComponent_initializers = [];
    let _inputComponent_extraInitializers = [];
    let __contextMenu_decorators;
    let __contextMenu_initializers = [];
    let __contextMenu_extraInitializers = [];
    let _selectItemsFromDoc_decorators;
    let _selectItemsFromDoc_initializers = [];
    let _selectItemsFromDoc_extraInitializers = [];
    let __selectAllItem_decorators;
    let __selectAllItem_initializers = [];
    let __selectAllItem_extraInitializers = [];
    let _set_value_decorators;
    let _set_focused_decorators;
    var SdsMultiselect = _classThis = class extends _classSuper {
        constructor() {
            super(...arguments);
            _SdsMultiselect_hint_accessor_storage.set(this, (__runInitializers(this, _instanceExtraInitializers), __runInitializers(this, _hint_initializers, '')));
            _SdsMultiselect_disabled_accessor_storage.set(this, (__runInitializers(this, _hint_extraInitializers), __runInitializers(this, _disabled_initializers, false)));
            _SdsMultiselect_withSelectAll_accessor_storage.set(this, (__runInitializers(this, _disabled_extraInitializers), __runInitializers(this, _withSelectAll_initializers, false)));
            _SdsMultiselect_simple_accessor_storage.set(this, (__runInitializers(this, _withSelectAll_extraInitializers), __runInitializers(this, _simple_initializers, false)));
            _SdsMultiselect_selectedItemsName_accessor_storage.set(this, (__runInitializers(this, _simple_extraInitializers), __runInitializers(this, _selectedItemsName_initializers, void 0)));
            _SdsMultiselect_placeholder_accessor_storage.set(this, (__runInitializers(this, _selectedItemsName_extraInitializers), __runInitializers(this, _placeholder_initializers, '')));
            _SdsMultiselect_errorMessage_accessor_storage.set(this, (__runInitializers(this, _placeholder_extraInitializers), __runInitializers(this, _errorMessage_initializers, '')));
            _SdsMultiselect_inputValue_accessor_storage.set(this, (__runInitializers(this, _errorMessage_extraInitializers), __runInitializers(this, _inputValue_initializers, '')));
            _SdsMultiselect_invalid_accessor_storage.set(this, (__runInitializers(this, _inputValue_extraInitializers), __runInitializers(this, _invalid_initializers, false)));
            _SdsMultiselect_maxWidth_accessor_storage.set(this, (__runInitializers(this, _invalid_extraInitializers), __runInitializers(this, _maxWidth_initializers, 400)));
            _SdsMultiselect_onlyIcon_accessor_storage.set(this, (__runInitializers(this, _maxWidth_extraInitializers), __runInitializers(this, _onlyIcon_initializers, false)));
            _SdsMultiselect_itemIcon_accessor_storage.set(this, (__runInitializers(this, _onlyIcon_extraInitializers), __runInitializers(this, _itemIcon_initializers, void 0)));
            _SdsMultiselect_trigger_accessor_storage.set(this, (__runInitializers(this, _itemIcon_extraInitializers), __runInitializers(this, _trigger_initializers, void 0)));
            _SdsMultiselect_inputComponent_accessor_storage.set(this, (__runInitializers(this, _trigger_extraInitializers), __runInitializers(this, _inputComponent_initializers, void 0)));
            _SdsMultiselect__contextMenu_accessor_storage.set(this, (__runInitializers(this, _inputComponent_extraInitializers), __runInitializers(this, __contextMenu_initializers, void 0)));
            this.menuItems = (__runInitializers(this, __contextMenu_extraInitializers), []);
            this.selectedItems = [];
            _SdsMultiselect_selectItemsFromDoc_accessor_storage.set(this, __runInitializers(this, _selectItemsFromDoc_initializers, void 0));
            this.lastScrollTopPosition = (__runInitializers(this, _selectItemsFromDoc_extraInitializers), 0);
            _SdsMultiselect__selectAllItem_accessor_storage.set(this, __runInitializers(this, __selectAllItem_initializers, void 0));
            this._focused = (__runInitializers(this, __selectAllItem_extraInitializers), false);
            _SdsMultiselect__value_accessor_storage.set(this, []);
            this.handleSelectItemPropertyChange = (event) => {
                const eventDetails = event.detail;
                const targetItem = event.target;
                if (!eventDetails || !(targetItem === null || targetItem === void 0 ? void 0 : targetItem.value)) {
                    return;
                }
                if (eventDetails.propertyName === 'selected') {
                    if (eventDetails.newValue) {
                        if (!this.selectedItems.some(item => item.value === targetItem.value)) {
                            this.selectedItems.push(targetItem);
                            this._value.push(targetItem.value);
                        }
                    }
                    else {
                        this.selectedItems = this.selectedItems.filter(item => item.value !== targetItem.value);
                        this._value = this._value.filter(value => value !== targetItem.value);
                    }
                    this.updateSelectedItemsInInput();
                    this.dispatchChangeEvent();
                    this.dispatchSelectedEvent();
                    this.syncSelectAllState();
                }
            };
            this.onContentChange = () => {
                this.menuItems = [];
                this.selectedItems = [];
                if (this.selectItemsFromDoc.length > 0) {
                    this.selectItemsFromDoc.forEach(item => {
                        this.setSelectItemValueIfNeeded(item);
                        this.validateUniqueSelectItemValue(item);
                        if (item instanceof SdsContextMenuSelectItem) {
                            this.selectItemFirstUpdate(item);
                            if (!item.selector) {
                                item.selector = 'checkbox';
                            }
                        }
                        else if (item instanceof SdsContextMenuGroup) {
                            item.content.forEach(groupItem => {
                                if (groupItem instanceof SdsContextMenuSelectItem) {
                                    this.setSelectItemValueIfNeeded(groupItem);
                                    this.validateUniqueSelectItemValue(groupItem);
                                    this.selectItemFirstUpdate(groupItem);
                                    if (!groupItem.selector) {
                                        groupItem.selector = 'checkbox';
                                    }
                                }
                            });
                        }
                        this.menuItems.push(item);
                    });
                    if (this.multiselectDropdownRenderer instanceof MultiselectRenderer) {
                        this.updateMenuContent();
                    }
                }
                this.updateSelectedItemsInInput();
                this.syncSelectAllState();
                this.requestUpdate();
            };
            this.handleMenuClose = (event) => {
                event.stopPropagation();
                const composedPath = event.composedPath();
                const isInsideSdsMultiselect = composedPath.some(el => el instanceof SdsMultiselect && el === this);
                if (!isInsideSdsMultiselect) {
                    this.multiselectDropdownRenderer.hideMenu(false); // Don't focus input when clicking outside
                }
            };
            this.onSelect = (event) => {
                var _a;
                const target = event.target;
                const eventDetails = event.detail;
                const contextMenu = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('sds-context-menu');
                if (!contextMenu || !target.value) {
                    return;
                }
                if (eventDetails.selected) {
                    this.selectItem(target);
                }
                else {
                    this.unselectItem(target);
                }
                cleanMenuItemsFocus(this._contextMenu);
                target.focused = true;
                this.multiselectDropdownRenderer.resetInputValue();
                this.focused = true;
                this.syncSelectAllState();
                this.dispatchSelectedEvent();
                this.dispatchChangeEvent();
            };
            this.handleScroll = () => {
                const menu = this._contextMenu.getMenu();
                if (this.lastScrollTopPosition < menu.scrollTop &&
                    2 * menu.offsetHeight + menu.scrollTop >= menu.scrollHeight) {
                    this.dispatchLoadMoreEvent();
                }
                this.lastScrollTopPosition = menu.scrollTop;
            };
            this.addEventListeners = () => {
                var _a, _b;
                document.addEventListener('keydown', this.multiselectDropdownRenderer.handleKeyDown);
                document.addEventListener('mouseup', this.handleMenuClose);
                window.addEventListener('resize', this.handleMenuClose);
                (_b = (_a = this._contextMenu.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.sds-context-menu')) === null || _b === void 0 ? void 0 : _b.addEventListener('scroll', this.handleScroll);
            };
            this.removeEventListeners = () => {
                var _a, _b;
                document.removeEventListener('keydown', this.multiselectDropdownRenderer.handleKeyDown);
                document.removeEventListener('mouseup', this.handleMenuClose);
                window.removeEventListener('resize', this.handleMenuClose);
                (_b = (_a = this._contextMenu.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('.sds-context-menu')) === null || _b === void 0 ? void 0 : _b.removeEventListener('scroll', this.handleScroll);
            };
            this.showContextMenu = () => {
                this.multiselectDropdownRenderer.openMenu();
            };
            this.selectAllHandler = (event) => {
                const selectAllItem = event.target;
                this.updateSelectAllState();
                cleanMenuItemsFocus(this._contextMenu);
                selectAllItem.focused = true;
            };
        }
        /**
         * Sets a hint for the multi-select input, providing context to users about its purpose or expected input.
         * This attribute enhances the user experience by guiding users in their selection process.
         *
         * @attr {string} hint - A hint text for the multi-select input.
         */
        get hint() { return __classPrivateFieldGet(this, _SdsMultiselect_hint_accessor_storage, "f"); }
        set hint(value) { __classPrivateFieldSet(this, _SdsMultiselect_hint_accessor_storage, value, "f"); }
        /**
         * Indicates whether the multi-select component is disabled, preventing any user interaction.
         * This attribute is useful for scenarios where the component should not be editable.
         *
         * @attr {boolean} disabled - True if the multi-select is disabled.
         */
        get disabled() { return __classPrivateFieldGet(this, _SdsMultiselect_disabled_accessor_storage, "f"); }
        set disabled(value) { __classPrivateFieldSet(this, _SdsMultiselect_disabled_accessor_storage, value, "f"); }
        /**
         * Specifies whether the multi-select should include a "Select All" option, allowing users
         * to select all items in the list with a single action.
         *
         * @attr {boolean} with-select-all - True if the multi-select includes a "Select All" option.
         */
        get withSelectAll() { return __classPrivateFieldGet(this, _SdsMultiselect_withSelectAll_accessor_storage, "f"); }
        set withSelectAll(value) { __classPrivateFieldSet(this, _SdsMultiselect_withSelectAll_accessor_storage, value, "f"); }
        /**
         * Toggles a simplified mode for the multi-select, which may alter its appearance or functionality.
         * This attribute can help streamline the user interface for specific use cases.
         *
         * @attr {boolean} simple - True if the multi-select is in simplified mode.
         */
        get simple() { return __classPrivateFieldGet(this, _SdsMultiselect_simple_accessor_storage, "f"); }
        set simple(value) { __classPrivateFieldSet(this, _SdsMultiselect_simple_accessor_storage, value, "f"); }
        /**
         * The name used to identify the currently selected items in the multi-select.
         * This attribute is useful for labeling the selected items in the user interface.
         *
         * @attr {string} selected-items-name - Name for the selected items in the multi-select.
         */
        get selectedItemsName() { return __classPrivateFieldGet(this, _SdsMultiselect_selectedItemsName_accessor_storage, "f"); }
        set selectedItemsName(value) { __classPrivateFieldSet(this, _SdsMultiselect_selectedItemsName_accessor_storage, value, "f"); }
        /**
         * Indicates whether any items are currently selected in the multi-select.
         */
        get selected() {
            return this.selectedItems.length > 0;
        }
        /**
         * Sets the placeholder text for the multi-select input field, providing guidance to users
         * when no items are selected. This attribute enhances accessibility and user experience.
         *
         * @attr {string} placeholder - Placeholder text for the multi-select input.
         */
        get placeholder() { return __classPrivateFieldGet(this, _SdsMultiselect_placeholder_accessor_storage, "f"); }
        set placeholder(value) { __classPrivateFieldSet(this, _SdsMultiselect_placeholder_accessor_storage, value, "f"); }
        /**
         * Displays an error message when the multi-select encounters a validation issue.
         * This attribute helps inform users of any issues with their selection.
         *
         * @attr {string} error-message - The error message displayed when validation fails.
         */
        get errorMessage() { return __classPrivateFieldGet(this, _SdsMultiselect_errorMessage_accessor_storage, "f"); }
        set errorMessage(value) { __classPrivateFieldSet(this, _SdsMultiselect_errorMessage_accessor_storage, value, "f"); }
        /**
         * Represents the current value of the multi-select input field, allowing for binding
         * to the selected options. This attribute is crucial for managing the component's state.
         *
         * @attr {string} input-value - The current value of the multi-select input.
         */
        get inputValue() { return __classPrivateFieldGet(this, _SdsMultiselect_inputValue_accessor_storage, "f"); }
        set inputValue(value) { __classPrivateFieldSet(this, _SdsMultiselect_inputValue_accessor_storage, value, "f"); }
        /**
         * Indicates whether the multi-select's selection is invalid, which can trigger
         * error styling or messaging. This attribute is essential for validation purposes.
         *
         * @attr {boolean} invalid - True if the selection is invalid.
         */
        get invalid() { return __classPrivateFieldGet(this, _SdsMultiselect_invalid_accessor_storage, "f"); }
        set invalid(value) { __classPrivateFieldSet(this, _SdsMultiselect_invalid_accessor_storage, value, "f"); }
        /**
         * Sets the maximum width of the multi-select component in pixels, allowing for responsive
         * design and control over its size.
         *
         * @attr {number} max-width - The maximum width of the multi-select in pixels.
         */
        get maxWidth() { return __classPrivateFieldGet(this, _SdsMultiselect_maxWidth_accessor_storage, "f"); }
        set maxWidth(value) { __classPrivateFieldSet(this, _SdsMultiselect_maxWidth_accessor_storage, value, "f"); }
        /**
         * Indicates if the multi-select should only display icons instead of text for its items.
         * This attribute can help streamline the appearance of the component in specific use cases.
         *
         * @attr {boolean} only-icon - True if the multi-select should only display icons.
         */
        get onlyIcon() { return __classPrivateFieldGet(this, _SdsMultiselect_onlyIcon_accessor_storage, "f"); }
        set onlyIcon(value) { __classPrivateFieldSet(this, _SdsMultiselect_onlyIcon_accessor_storage, value, "f"); }
        get itemIcon() { return __classPrivateFieldGet(this, _SdsMultiselect_itemIcon_accessor_storage, "f"); }
        set itemIcon(value) { __classPrivateFieldSet(this, _SdsMultiselect_itemIcon_accessor_storage, value, "f"); }
        get trigger() { return __classPrivateFieldGet(this, _SdsMultiselect_trigger_accessor_storage, "f"); }
        set trigger(value) { __classPrivateFieldSet(this, _SdsMultiselect_trigger_accessor_storage, value, "f"); }
        get inputComponent() { return __classPrivateFieldGet(this, _SdsMultiselect_inputComponent_accessor_storage, "f"); }
        set inputComponent(value) { __classPrivateFieldSet(this, _SdsMultiselect_inputComponent_accessor_storage, value, "f"); }
        get _contextMenu() { return __classPrivateFieldGet(this, _SdsMultiselect__contextMenu_accessor_storage, "f"); }
        set _contextMenu(value) { __classPrivateFieldSet(this, _SdsMultiselect__contextMenu_accessor_storage, value, "f"); }
        get selectItemsFromDoc() { return __classPrivateFieldGet(this, _SdsMultiselect_selectItemsFromDoc_accessor_storage, "f"); }
        set selectItemsFromDoc(value) { __classPrivateFieldSet(this, _SdsMultiselect_selectItemsFromDoc_accessor_storage, value, "f"); }
        get _selectAllItem() { return __classPrivateFieldGet(this, _SdsMultiselect__selectAllItem_accessor_storage, "f"); }
        set _selectAllItem(value) { __classPrivateFieldSet(this, _SdsMultiselect__selectAllItem_accessor_storage, value, "f"); }
        /**
         * Selected items value.
         *
         * Holds the normalized array of selected items value.
         * Converts an incoming comma-separated value string into an array.
         * Use the public `value` getter/setter for external access and synchronization.
         * @attr {string | string[]} value - Selected items value as array or string (e.g., "item1,item2")
         *  *
         */
        get _value() { return __classPrivateFieldGet(this, _SdsMultiselect__value_accessor_storage, "f"); }
        set _value(value) { __classPrivateFieldSet(this, _SdsMultiselect__value_accessor_storage, value, "f"); }
        get value() {
            if (this.selectedItems.length > 0) {
                this._value = this.selectedItems
                    .map(item => { var _a; return (_a = item.value) !== null && _a !== void 0 ? _a : ''; })
                    .filter(value => value !== '');
            }
            return this._value;
        }
        set value(value) {
            if (value === null) {
                this._value = [];
            }
            else if (typeof value === 'string') {
                this._value = value
                    .split(',')
                    .map(v => v.trim())
                    .filter(v => v);
            }
            else {
                this._value = value;
            }
            this.selectedItems = [];
            this.selectItemStateHandler(item => {
                if (item.value && this._value.includes(item.value)) {
                    item.setSelectedSilently(true);
                    this.selectedItems.push(item);
                }
                else {
                    item.setSelectedSilently(false);
                }
            });
            this.updateSelectedItemsInInput();
            this.syncSelectAllState();
            this.dispatchChangeEvent();
        }
        get selectAllItem() {
            var _a;
            return (this._selectAllItem ||
                ((_a = this._contextMenu.getMenu()) === null || _a === void 0 ? void 0 : _a.querySelector('sds-cm-select-item[id="select-all-item"]')));
        }
        dispatchSelectedEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.SELECTED_EVENT, {
                detail: {
                    identifiers: new Set(this.selectedItems.map(item => item.identifier || '')),
                },
                bubbles: false,
                composed: false,
            }));
        }
        dispatchInputEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.INPUT_EVENT, {
                detail: {
                    value: this.inputValue,
                },
                bubbles: false,
                composed: false,
            }));
            this.dispatchEvent(new CustomEvent(SdsMultiselect.NATIVE_INPUT_EVENT, {
                detail: {
                    value: this.inputValue,
                },
                bubbles: true,
                composed: true,
            }));
        }
        dispatchChangeEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.CHANGE, {
                detail: {
                    value: new Set(this.selectedItems.map(item => item.value || '')),
                },
                bubbles: false,
                composed: false,
            }));
        }
        get focused() {
            return this._focused;
        }
        set focused(value) {
            this._focused = value;
            if (this.inputComponent) {
                this.inputComponent.focused = value;
                if (value) {
                    this.inputComponent.getInput().focus();
                }
                else {
                    this.inputComponent.getInput().blur();
                }
            }
        }
        willUpdate(_changedProperties) {
            if (_changedProperties.has('simple')) {
                this.multiselectDropdownRenderer = this.simple
                    ? new SimpleMultiselectRenderer(this)
                    : new MultiselectRenderer(this);
            }
            this.multiselectDropdownRenderer.willUpdate(_changedProperties);
        }
        async firstUpdated() {
            await this.updateComplete;
            this.multiselectDropdownRenderer.firstUpdated();
            this.initializeSelectedItems();
        }
        render() {
            return this.multiselectDropdownRenderer.render();
        }
        addEventListener(type, listener, options) {
            super.addEventListener(type, listener, options);
        }
        handleEnterKeydown(event) {
            var _a;
            this.selectItemStateHandler(item => {
                if (item.focused && !item.disabled) {
                    if (item.selected && item.value) {
                        this.multiselectDropdownRenderer.resetInputValue();
                        item.setSelectedSilently(false);
                        this.unselectItem(item);
                    }
                    else {
                        this.multiselectDropdownRenderer.resetInputValue();
                        item.setSelectedSilently(true);
                        if (item.value) {
                            this.selectItem(item);
                        }
                    }
                    this.syncSelectAllState();
                    this.dispatchSelectedEvent();
                    this.dispatchChangeEvent();
                }
            });
            if ((_a = this.selectAllItem) === null || _a === void 0 ? void 0 : _a.focused) {
                this.updateSelectAllState(!this.selectAllItem.selected);
            }
            event.preventDefault();
            event.stopPropagation();
        }
        initializeSelectedItems() {
            const menuItems = this.selectItemsFromDoc;
            menuItems.forEach(item => {
                if (item instanceof SdsContextMenuSelectItem) {
                    this.selectItemFirstUpdate(item);
                }
            });
        }
        selectItemFirstUpdate(selectItem) {
            if (this._value.length > 0) {
                if (this._value.some(value => selectItem.value === value)) {
                    selectItem.setSelectedSilently(true);
                    if (!this.selectedItems.some(item => item.value === selectItem.value)) {
                        this.selectedItems.push(selectItem);
                        this.applySelectedItemToInput(selectItem);
                    }
                }
                else {
                    selectItem.setSelectedSilently(false);
                }
            }
            else if (selectItem.value && selectItem.selected) {
                if (!this.selectedItems.some(item => item.value === selectItem.value)) {
                    this.selectedItems.push(selectItem);
                    this.applySelectedItemToInput(selectItem);
                }
            }
            selectItem.addEventListener(SdsContextMenuSelectItem.CM_ITEM_SELECTION_CHANGE, this.onSelect);
            selectItem.addEventListener(SdsContextMenuItem.CM_ITEM_PROPERTY_CHANGE, this.handleSelectItemPropertyChange);
        }
        dispatchLoadMoreEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.LOAD_MORE_EVENT, {
                bubbles: false,
                composed: false,
            }));
        }
        dispatchOpenEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.OPEN_EVENT, {
                bubbles: false,
                composed: false,
            }));
        }
        dispatchCloseEvent() {
            this.dispatchEvent(new CustomEvent(SdsMultiselect.CLOSE_EVENT, {
                bubbles: false,
                composed: false,
            }));
        }
        updateMenuContent() {
            var _a;
            (_a = this.menuItems) === null || _a === void 0 ? void 0 : _a.map(item => {
                if (!(item instanceof SdsContextMenuSkeletonItem)) {
                    const sortInfo = item.applySearchAndReturnSortInfo(this.inputValue);
                    return {
                        sortInfo: sortInfo,
                        item: item,
                    };
                }
                else {
                    return {
                        sortInfo: SortInfo.LAST,
                        item: item,
                    };
                }
            }).filter(meta => {
                const isFound = meta.sortInfo.isFound();
                meta.item.style.display = isFound ? '' : 'none';
                return isFound;
            }).sort((a, b) => SortInfo.compareFn(a.sortInfo, b.sortInfo));
            cleanMenuItemsFocus(this._contextMenu);
            initMenuItemsFocus(this._contextMenu);
        }
        selectItemStateHandler(callback) {
            this.menuItems.forEach(item => {
                if (item instanceof SdsContextMenuSelectItem) {
                    callback(item);
                }
                if (item instanceof SdsContextMenuGroup) {
                    item.content.forEach(groupItem => {
                        if (groupItem instanceof SdsContextMenuSelectItem) {
                            callback(groupItem);
                        }
                    });
                }
            });
        }
        setSelectItemValueIfNeeded(item) {
            if (!item.value) {
                item.value = uniqueId('multiselect-item-');
            }
        }
        applySelectedItemToInput(item) {
            if (this.simple) {
                const selectedNames = this.selectedItems.map(item => item.name);
                this.selectedItemsName = selectedNames.join(', ');
                return this.selectedItemsName;
            }
            else if (item.value) {
                return this.inputComponent.addLabel(item.value, this.multiselectDropdownRenderer.getLabelMetadata(item));
            }
        }
        updateSelectedItemsInInput() {
            var _a;
            if (this.simple) {
                const selectedNames = this.selectedItems.map(item => item.name);
                this.selectedItemsName = selectedNames.join(', ');
            }
            else {
                (_a = this.inputComponent) === null || _a === void 0 ? void 0 : _a.clearLabels();
                this.selectedItems.forEach(item => {
                    if (item.value) {
                        this.inputComponent.addLabel(item.value, this.multiselectDropdownRenderer.getLabelMetadata(item));
                    }
                });
            }
        }
        selectItem(item) {
            if (!isNil(item.value)) {
                if (!this.selectedItems.some(selectedItem => selectedItem.value === item.value)) {
                    this.selectedItems.push(item);
                    this.applySelectedItemToInput(item);
                    // Set selection action for accessibility
                    this.multiselectDropdownRenderer.setSelectionAction('selected');
                }
            }
        }
        unselectItem(item) {
            if (!isNil(item.value)) {
                this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.value !== item.value);
                // Update _value to match selectedItems
                this.updateValueFromSelectedItems();
                if (this.simple) {
                    this.updateSelectedItemsInInput();
                }
                else {
                    this.inputComponent.deleteLabel(item.value);
                }
                this.multiselectDropdownRenderer.setSelectionAction('unselected');
            }
        }
        updateValueFromSelectedItems() {
            this._value = this.selectedItems.filter(item => item.value).map(item => item.value);
        }
        renderSelectAll() {
            if (this.withSelectAll && this.menuItems.length > 0) {
                return html `<sds-cm-select-item
                id="select-all-item"
                name="${translate('sds.multiselect.select_all')}"
                selector="checkbox"
                with-select-all
                style="display: ${!this.isAnyItemHidden() ? '' : 'none'}"
                @sds.cm.item.selection.change="${this.selectAllHandler}"
            >
            </sds-cm-select-item>`;
            }
            return html ``;
        }
        isAnyItemHidden() {
            return this.menuItems.some(item => item.style.display === 'none');
        }
        updateSelectAllState(selected) {
            if (this.selectAllItem.indeterminate) {
                this.selectAllItem.indeterminate = false;
                this.selectAllItem.setSelectedSilently(true);
            }
            const newSelected = selected !== null && selected !== void 0 ? selected : this.selectAllItem.selected;
            this.selectAllItem.setSelectedSilently(newSelected);
            this.getMenuItemsWithGroupsFlattened().forEach(item => {
                item.setSelectedSilently(this.selectAllItem.selected);
                item.selected ? this.selectItem(item) : this.unselectItem(item);
            });
            // Set selection action for accessibility
            const action = this.selectAllItem.selected ? 'selected' : 'unselected';
            this.multiselectDropdownRenderer.setSelectionAction(action);
            this.dispatchSelectedEvent();
            this.dispatchChangeEvent();
        }
        syncSelectAllState() {
            if (this.withSelectAll && !isNil(this.selectAllItem)) {
                const itemsWithoutGroups = this.getMenuItemsWithGroupsFlattened().length;
                if (this.selectedItems.length === 0) {
                    this.selectAllItem.setSelectedSilently(false);
                    this.selectAllItem.indeterminate = false;
                }
                else if (this.selectedItems.length === itemsWithoutGroups) {
                    this.selectAllItem.setSelectedSilently(true);
                    this.selectAllItem.indeterminate = false;
                }
                else if (this.selectedItems.length > 0) {
                    this.selectAllItem.setSelectedSilently(false);
                    this.selectAllItem.indeterminate = true;
                }
            }
        }
        getMenuItemsWithGroupsFlattened() {
            return this.menuItems.flatMap(item => {
                if (item instanceof SdsContextMenuGroup) {
                    return item.content.filter(child => child instanceof SdsContextMenuSelectItem);
                }
                return item instanceof SdsContextMenuSelectItem ? [item] : [];
            });
        }
        validateUniqueSelectItemValue(multiselectItem) {
            const allSelectItems = this.menuItems.flatMap(item => {
                if (item instanceof SdsContextMenuGroup && Array.isArray(item.content)) {
                    return [...item.content, item];
                }
                else {
                    return [item];
                }
            });
            const isDuplicate = allSelectItems.some(item => item.value === multiselectItem.value);
            if (isDuplicate) {
                console.error(`Select item with value '${multiselectItem.value}' already exists.`);
            }
        }
        /**
         * Sets the ariaActiveDescendantElement property on the input element.
         * This is used for cross-shadow-root accessibility communication.
         *
         * The ariaActiveDescendantElement property allows the input to reference
         * a menu item element directly (instead of using string IDs), which works
         * across shadow DOM boundaries where traditional aria-activedescendant fails.
         *
         * @param element - The menu item element to reference, or null to clear
         * @private
         */
        setAriaActiveDescendantElement(element) {
            var _a;
            const inputElement = (_a = this.inputComponent) === null || _a === void 0 ? void 0 : _a.getInput();
            if (inputElement) {
                // Use bracket notation to access the non-standard DOM property
                // ariaActiveDescendantElement is a modern browser feature for aria-activedescendant
                inputElement['ariaActiveDescendantElement'] = element;
            }
        }
        /**
         * Updates the aria-activedescendant reference to point to the specified menu item.
         * This enables screen readers to announce the currently navigated item while
         * keeping focus on the input for typing/searching.
         *
         * @param menuItem - The menu item element that is currently focused/navigated
         * @public
         */
        updateAriaActiveDescendant(menuItem) {
            if (menuItem) {
                this.setAriaActiveDescendantElement(menuItem);
            }
        }
        /**
         * Clears the aria-activedescendant reference, typically when the menu closes
         * or when no item is focused.
         *
         * @public
         */
        clearAriaActiveDescendant() {
            this.setAriaActiveDescendantElement(null);
        }
        /**
         * Sets the initial aria-activedescendant reference when the menu opens.
         * This points to whichever item is initially focused (usually the first item
         * or a previously selected item).
         *
         * @public
         */
        setInitialAriaActiveDescendant() {
            const focusedItem = getFocusedItem(this._contextMenu);
            if (focusedItem) {
                this.updateAriaActiveDescendant(focusedItem);
            }
        }
        focus() {
            if (this.simple) {
                if (this.trigger instanceof HTMLElement) {
                    this.trigger.focus();
                    this.focused = true;
                    this.dispatchEvent(new CustomEvent(SdsMultiselect.FOCUS_EVENT, {
                        bubbles: false,
                        composed: false,
                    }));
                }
            }
            else if (this.inputComponent) {
                this.inputComponent.focus();
                this.focused = true;
                this.dispatchEvent(new CustomEvent(SdsMultiselect.FOCUS_EVENT, {
                    bubbles: false,
                    composed: false,
                }));
            }
        }
        blur() {
            if (this.simple) {
                if (this.trigger instanceof HTMLElement) {
                    this.trigger.blur();
                    this.focused = false;
                    this.dispatchEvent(new CustomEvent(SdsMultiselect.BLUR_EVENT, {
                        bubbles: false,
                        composed: false,
                    }));
                }
            }
            else if (this.inputComponent) {
                this.inputComponent.blur();
                this.focused = false;
                this.dispatchEvent(new CustomEvent(SdsMultiselect.BLUR_EVENT, {
                    bubbles: false,
                    composed: false,
                }));
            }
        }
        click() {
            if (this.simple) {
                if (this.trigger instanceof HTMLElement) {
                    this.trigger.focus();
                    if (this.multiselectDropdownRenderer.toggleContextMenu) {
                        this.multiselectDropdownRenderer.toggleContextMenu();
                    }
                }
            }
            else if (this.inputComponent) {
                const inputElement = this.inputComponent.getInput();
                if (inputElement) {
                    inputElement.focus();
                    inputElement.click();
                }
                this.inputComponent.setCursorToEnd();
                this.multiselectDropdownRenderer.openMenu();
            }
        }
    };
    _SdsMultiselect_hint_accessor_storage = new WeakMap();
    _SdsMultiselect_disabled_accessor_storage = new WeakMap();
    _SdsMultiselect_withSelectAll_accessor_storage = new WeakMap();
    _SdsMultiselect_simple_accessor_storage = new WeakMap();
    _SdsMultiselect_selectedItemsName_accessor_storage = new WeakMap();
    _SdsMultiselect_placeholder_accessor_storage = new WeakMap();
    _SdsMultiselect_errorMessage_accessor_storage = new WeakMap();
    _SdsMultiselect_inputValue_accessor_storage = new WeakMap();
    _SdsMultiselect_invalid_accessor_storage = new WeakMap();
    _SdsMultiselect_maxWidth_accessor_storage = new WeakMap();
    _SdsMultiselect_onlyIcon_accessor_storage = new WeakMap();
    _SdsMultiselect_itemIcon_accessor_storage = new WeakMap();
    _SdsMultiselect_trigger_accessor_storage = new WeakMap();
    _SdsMultiselect_inputComponent_accessor_storage = new WeakMap();
    _SdsMultiselect__contextMenu_accessor_storage = new WeakMap();
    _SdsMultiselect_selectItemsFromDoc_accessor_storage = new WeakMap();
    _SdsMultiselect__selectAllItem_accessor_storage = new WeakMap();
    _SdsMultiselect__value_accessor_storage = new WeakMap();
    __setFunctionName(_classThis, "SdsMultiselect");
    (() => {
        var _a;
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create((_a = _classSuper[Symbol.metadata]) !== null && _a !== void 0 ? _a : null) : void 0;
        _hint_decorators = [property({ type: String })];
        _disabled_decorators = [property({ type: Boolean })];
        _withSelectAll_decorators = [property({ type: Boolean, attribute: 'with-select-all' })];
        _simple_decorators = [property({ type: Boolean })];
        _selectedItemsName_decorators = [property({ type: String })];
        _placeholder_decorators = [property({ type: String })];
        _errorMessage_decorators = [property({ type: String, attribute: 'error-message' })];
        _inputValue_decorators = [state()];
        _invalid_decorators = [property({ type: Boolean })];
        _maxWidth_decorators = [property({ type: Number, attribute: 'max-width' })];
        _onlyIcon_decorators = [property({ type: Boolean, attribute: 'only-icon' })];
        _itemIcon_decorators = [property({ attribute: false })];
        _trigger_decorators = [query('#trigger')];
        _inputComponent_decorators = [query('sds-input-multiselect')];
        __contextMenu_decorators = [query('sds-context-menu')];
        _selectItemsFromDoc_decorators = [queryAssignedElements({ flatten: false })];
        __selectAllItem_decorators = [query('#select-all-item')];
        _set_value_decorators = [property()];
        _set_focused_decorators = [property({ type: Boolean })];
        __esDecorate(_classThis, null, _hint_decorators, { kind: "accessor", name: "hint", static: false, private: false, access: { has: obj => "hint" in obj, get: obj => obj.hint, set: (obj, value) => { obj.hint = value; } }, metadata: _metadata }, _hint_initializers, _hint_extraInitializers);
        __esDecorate(_classThis, null, _disabled_decorators, { kind: "accessor", name: "disabled", static: false, private: false, access: { has: obj => "disabled" in obj, get: obj => obj.disabled, set: (obj, value) => { obj.disabled = value; } }, metadata: _metadata }, _disabled_initializers, _disabled_extraInitializers);
        __esDecorate(_classThis, null, _withSelectAll_decorators, { kind: "accessor", name: "withSelectAll", static: false, private: false, access: { has: obj => "withSelectAll" in obj, get: obj => obj.withSelectAll, set: (obj, value) => { obj.withSelectAll = value; } }, metadata: _metadata }, _withSelectAll_initializers, _withSelectAll_extraInitializers);
        __esDecorate(_classThis, null, _simple_decorators, { kind: "accessor", name: "simple", static: false, private: false, access: { has: obj => "simple" in obj, get: obj => obj.simple, set: (obj, value) => { obj.simple = value; } }, metadata: _metadata }, _simple_initializers, _simple_extraInitializers);
        __esDecorate(_classThis, null, _selectedItemsName_decorators, { kind: "accessor", name: "selectedItemsName", static: false, private: false, access: { has: obj => "selectedItemsName" in obj, get: obj => obj.selectedItemsName, set: (obj, value) => { obj.selectedItemsName = value; } }, metadata: _metadata }, _selectedItemsName_initializers, _selectedItemsName_extraInitializers);
        __esDecorate(_classThis, null, _placeholder_decorators, { kind: "accessor", name: "placeholder", static: false, private: false, access: { has: obj => "placeholder" in obj, get: obj => obj.placeholder, set: (obj, value) => { obj.placeholder = value; } }, metadata: _metadata }, _placeholder_initializers, _placeholder_extraInitializers);
        __esDecorate(_classThis, null, _errorMessage_decorators, { kind: "accessor", name: "errorMessage", static: false, private: false, access: { has: obj => "errorMessage" in obj, get: obj => obj.errorMessage, set: (obj, value) => { obj.errorMessage = value; } }, metadata: _metadata }, _errorMessage_initializers, _errorMessage_extraInitializers);
        __esDecorate(_classThis, null, _inputValue_decorators, { kind: "accessor", name: "inputValue", static: false, private: false, access: { has: obj => "inputValue" in obj, get: obj => obj.inputValue, set: (obj, value) => { obj.inputValue = value; } }, metadata: _metadata }, _inputValue_initializers, _inputValue_extraInitializers);
        __esDecorate(_classThis, null, _invalid_decorators, { kind: "accessor", name: "invalid", static: false, private: false, access: { has: obj => "invalid" in obj, get: obj => obj.invalid, set: (obj, value) => { obj.invalid = value; } }, metadata: _metadata }, _invalid_initializers, _invalid_extraInitializers);
        __esDecorate(_classThis, null, _maxWidth_decorators, { kind: "accessor", name: "maxWidth", static: false, private: false, access: { has: obj => "maxWidth" in obj, get: obj => obj.maxWidth, set: (obj, value) => { obj.maxWidth = value; } }, metadata: _metadata }, _maxWidth_initializers, _maxWidth_extraInitializers);
        __esDecorate(_classThis, null, _onlyIcon_decorators, { kind: "accessor", name: "onlyIcon", static: false, private: false, access: { has: obj => "onlyIcon" in obj, get: obj => obj.onlyIcon, set: (obj, value) => { obj.onlyIcon = value; } }, metadata: _metadata }, _onlyIcon_initializers, _onlyIcon_extraInitializers);
        __esDecorate(_classThis, null, _itemIcon_decorators, { kind: "accessor", name: "itemIcon", static: false, private: false, access: { has: obj => "itemIcon" in obj, get: obj => obj.itemIcon, set: (obj, value) => { obj.itemIcon = value; } }, metadata: _metadata }, _itemIcon_initializers, _itemIcon_extraInitializers);
        __esDecorate(_classThis, null, _trigger_decorators, { kind: "accessor", name: "trigger", static: false, private: false, access: { has: obj => "trigger" in obj, get: obj => obj.trigger, set: (obj, value) => { obj.trigger = value; } }, metadata: _metadata }, _trigger_initializers, _trigger_extraInitializers);
        __esDecorate(_classThis, null, _inputComponent_decorators, { kind: "accessor", name: "inputComponent", static: false, private: false, access: { has: obj => "inputComponent" in obj, get: obj => obj.inputComponent, set: (obj, value) => { obj.inputComponent = value; } }, metadata: _metadata }, _inputComponent_initializers, _inputComponent_extraInitializers);
        __esDecorate(_classThis, null, __contextMenu_decorators, { kind: "accessor", name: "_contextMenu", static: false, private: false, access: { has: obj => "_contextMenu" in obj, get: obj => obj._contextMenu, set: (obj, value) => { obj._contextMenu = value; } }, metadata: _metadata }, __contextMenu_initializers, __contextMenu_extraInitializers);
        __esDecorate(_classThis, null, _selectItemsFromDoc_decorators, { kind: "accessor", name: "selectItemsFromDoc", static: false, private: false, access: { has: obj => "selectItemsFromDoc" in obj, get: obj => obj.selectItemsFromDoc, set: (obj, value) => { obj.selectItemsFromDoc = value; } }, metadata: _metadata }, _selectItemsFromDoc_initializers, _selectItemsFromDoc_extraInitializers);
        __esDecorate(_classThis, null, __selectAllItem_decorators, { kind: "accessor", name: "_selectAllItem", static: false, private: false, access: { has: obj => "_selectAllItem" in obj, get: obj => obj._selectAllItem, set: (obj, value) => { obj._selectAllItem = value; } }, metadata: _metadata }, __selectAllItem_initializers, __selectAllItem_extraInitializers);
        __esDecorate(_classThis, null, _set_value_decorators, { kind: "setter", name: "value", static: false, private: false, access: { has: obj => "value" in obj, set: (obj, value) => { obj.value = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
        __esDecorate(_classThis, null, _set_focused_decorators, { kind: "setter", name: "focused", static: false, private: false, access: { has: obj => "focused" in obj, set: (obj, value) => { obj.focused = value; } }, metadata: _metadata }, null, _instanceExtraInitializers);
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        SdsMultiselect = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
    })();
    _classThis.styles = styles;
    _classThis.CHANGE = 'change';
    _classThis.FOCUS_EVENT = 'focus';
    _classThis.BLUR_EVENT = 'blur';
    _classThis.CLICK_EVENT = 'click';
    _classThis.NATIVE_INPUT_EVENT = 'input';
    _classThis.CLEARED_EVENT = 'sds.multiselect.cleared';
    _classThis.SELECTED_EVENT = 'sds.multiselect.selected';
    _classThis.INPUT_EVENT = 'sds.multiselect.input';
    _classThis.LOAD_MORE_EVENT = 'sds.multiselect.load.more';
    _classThis.OPEN_EVENT = 'sds.multiselect.opened';
    _classThis.CLOSE_EVENT = 'sds.multiselect.closed';
    (() => {
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return SdsMultiselect = _classThis;
})();
export { SdsMultiselect };
